import React, { useState, useCallback, useEffect, useMemo } from 'react';
import { Template, ABTest, TestableElement, ElementSuggestion, ABTestHistory, ABTestConfiguration, SuccessMetric, ABTestVariant, VariantChange } from '../../../types/template';
import VariantEditor from './VariantEditor';
import { tone, toneForPriority } from "components/ui/tone";
import { SelectField } from "components/ui/select-field";

interface ABTestManagerProps {
  template: Template;
  selectedBlockId: string | null;
  onCreateTest: (testConfig: Partial<ABTest>) => void;
  onMarkTestableElement: (blockId: string, testType: TestableElement['testType']) => void;
  onTransitionToABTestView: (testId: string, context: any) => void;
  isVisible: boolean;
  onClose: () => void;
}

interface ElementGroup {
  id: string;
  name: string;
  elements: TestableElement[];
  isSelected: boolean;
}

interface AIAnalysisResult {
  overallScore: number;
  recommendations: string[];
  priorityElements: TestableElement[];
  estimatedImpact: number;
}

const ABTestManager: React.FC<ABTestManagerProps> = ({
  template,
  selectedBlockId: _selectedBlockId,
  onCreateTest,
  onMarkTestableElement,
  onTransitionToABTestView,
  isVisible,
  onClose,
}) => {
  // State for testable elements and analysis
  const [testableElements, setTestableElements] = useState<TestableElement[]>([]);
  const [elementGroups, setElementGroups] = useState<ElementGroup[]>([]);
  const [aiAnalysis, setAiAnalysis] = useState<AIAnalysisResult | null>(null);
  const [testHistory, setTestHistory] = useState<ABTestHistory | null>(null);

  // UI state
  const [activePanel, setActivePanel] = useState<'elements' | 'configuration' | 'insights'>('elements');
  const [showAIAnalysis, setShowAIAnalysis] = useState(true);
  const [bulkSelectMode, setBulkSelectMode] = useState(false);
  const [_selectedGroup, _setSelectedGroup] = useState<string | null>(null);
  const [filterPriority, setFilterPriority] = useState<'all' | 'high' | 'medium' | 'low'>('all');
  const [searchQuery, setSearchQuery] = useState('');

  // Variant Editor state
  const [showVariantEditor, setShowVariantEditor] = useState(false);
  const [testVariants, setTestVariants] = useState<ABTestVariant[]>([]);
  const [activeVariantId, setActiveVariantId] = useState<string | null>(null);

  // Test configuration state
  const [testConfiguration, setTestConfiguration] = useState<ABTestConfiguration>({
    trafficSplit: [50, 50],
    successMetrics: [
      {
        id: 'click-rate',
        name: 'Click Rate',
        type: 'click_rate',
        isPrimary: true,
        weight: 1,
      }
    ],
    duration: {
      type: 'until_significant',
      days: 14,
    },
    statisticalSettings: {
      confidenceLevel: 95,
      minimumDetectableEffect: 10,
      powerAnalysis: 80,
    }
  });

  const [testName, setTestName] = useState('');
  const [testDescription, setTestDescription] = useState('');

  // Initialize testable elements analysis and AI analysis
  useEffect(() => {
    if (template) {
      analyzeTestableElements();
      loadTestHistory();
    } else {
      // Create demo elements for demonstration
      const demoElements: TestableElement[] = [
        {
          blockId: 'demo-button-1',
          blockType: 'button' as const,
          elementName: 'Demo CTA Button',
          testType: 'cta' as const,
          suggestions: getDefaultSuggestions('button'),
          priority: 'high' as const,
          isMarked: false,
        },
        {
          blockId: 'demo-text-1',
          blockType: 'text' as const,
          elementName: 'Demo Headline',
          testType: 'content' as const,
          suggestions: getDefaultSuggestions('text'),
          priority: 'medium' as const,
          isMarked: false,
        },
        {
          blockId: 'demo-image-1',
          blockType: 'image' as const,
          elementName: 'Hero Image',
          testType: 'image' as const,
          suggestions: getDefaultSuggestions('image'),
          priority: 'medium' as const,
          isMarked: false,
        }
      ];
      setTestableElements(demoElements);
      generateElementGroupsFromElements(demoElements);
      performAIAnalysisFromElements(demoElements);
    }

    // Set default test name
    setTestName(`${template?.name || 'Template'} A/B Test - ${new Date().toLocaleDateString()}`);
  }, [template]);

  // Helper functions for AI analysis and element grouping
  const generateElementGroupsFromElements = useCallback((elements: TestableElement[]) => {
    const groups: ElementGroup[] = [
      {
        id: 'high-priority',
        name: 'High Priority Elements',
        elements: elements.filter(el => el.priority === 'high'),
        isSelected: false,
      },
      {
        id: 'cta-elements',
        name: 'Call-to-Action Elements',
        elements: elements.filter(el => el.testType === 'cta'),
        isSelected: false,
      },
      {
        id: 'content-elements',
        name: 'Content Elements',
        elements: elements.filter(el => el.testType === 'content'),
        isSelected: false,
      },
      {
        id: 'visual-elements',
        name: 'Visual Elements',
        elements: elements.filter(el => el.testType === 'image'),
        isSelected: false,
      }
    ].filter(group => group.elements.length > 0);

    setElementGroups(groups);
  }, []);

  const performAIAnalysisFromElements = useCallback((elements: TestableElement[]) => {
    const highPriorityElements = elements.filter(el => el.priority === 'high');
    const avgConfidence = elements.reduce((sum, el) => {
      const avgElementConfidence = el.suggestions.reduce((s, sug) => s + sug.confidence, 0) / el.suggestions.length;
      return sum + avgElementConfidence;
    }, 0) / elements.length;

    const estimatedImpact = elements.reduce((sum, el) => {
      const avgElementImpact = el.suggestions.reduce((s, sug) => s + sug.impactMagnitude, 0) / el.suggestions.length;
      return sum + avgElementImpact;
    }, 0) / elements.length;

    const analysis: AIAnalysisResult = {
      overallScore: Math.round((avgConfidence + estimatedImpact) / 2),
      recommendations: [
        `Focus on ${highPriorityElements.length} high-priority elements for maximum impact`,
        `Expected improvement range: ${Math.round(estimatedImpact * 0.8)}-${Math.round(estimatedImpact * 1.2)}%`,
        `Recommended test duration: ${estimatedImpact > 15 ? '7-14' : '14-21'} days`,
      ],
      priorityElements: highPriorityElements,
      estimatedImpact: Math.round(estimatedImpact),
    };

    setAiAnalysis(analysis);
  }, []);

  // Separate effect to handle AI analysis after testable elements are set
  useEffect(() => {
    if (testableElements.length > 0) {
      performAIAnalysisFromElements(testableElements);
      generateElementGroupsFromElements(testableElements);
    }
  }, [testableElements, performAIAnalysisFromElements, generateElementGroupsFromElements]);

  // Enhanced AI-powered element analysis
  const analyzeTestableElements = useCallback(() => {
    const elements: TestableElement[] = [];

    if (!template?.content || template.content.length === 0) {
      // For empty templates, show guidance on adding content first
      setTestableElements([]);
      return;
    }

    template.content.forEach(block => {
      const suggestions = generateElementSuggestions(block);
      const testableTypes = [
        'button', 'text', 'image', 'columns', 'product', 'social', 'footer',
        'custom_html', 'dynamic_content', 'template_part', 'dynamic_name',
        'dynamic_date', 'custom_field', 'conditional_content', 'spacer', 'divider'
      ];

      if (testableTypes.includes(block.type)) {
        elements.push({
          blockId: block.id,
          blockType: block.type,
          elementName: block.metadata?.name || `${block.type.charAt(0).toUpperCase() + block.type.slice(1)} Element`,
          testType: getRecommendedTestType(block.type),
          suggestions: suggestions.length > 0 ? suggestions : getDefaultSuggestions(block.type),
          priority: suggestions.length > 0 ? calculatePriority(block, suggestions) : 'medium',
          isMarked: false,
        });
      }
    });

    setTestableElements(elements);
  }, [template]);

  // Generate element groups for bulk operations
  const _generateElementGroups = useCallback(() => {
    if (testableElements.length === 0) return;
    generateElementGroupsFromElements(testableElements);
  }, [testableElements]);

  // AI analysis for overall test recommendations
  const _performAIAnalysis = useCallback(() => {
    if (testableElements.length === 0) return;
    performAIAnalysisFromElements(testableElements);
  }, [testableElements]);

  const generateElementSuggestions = (block: any): ElementSuggestion[] => {
    const suggestions: ElementSuggestion[] = [];

    switch (block.type) {
      case 'button':
        suggestions.push(
          {
            id: `${block.id}-cta-text`,
            type: 'best_practice',
            title: 'Test CTA Text Variations',
            description: 'Try action-oriented vs benefit-focused button text',
            suggestedChange: {
              variations: ['Get Started Now', 'Start Your Free Trial', 'Learn More', 'Join Today']
            },
            confidence: 85,
            expectedImpact: 'positive',
            impactMagnitude: 15,
          },
          {
            id: `${block.id}-button-color`,
            type: 'ai_generated',
            title: 'Test Button Color',
            description: 'Test high-contrast colors for better visibility',
            suggestedChange: {
              colors: ['#FF6B35', '#2ECC71', '#E74C3C', '#3498DB']
            },
            confidence: 70,
            expectedImpact: 'positive',
            impactMagnitude: 8,
          }
        );
        break;

      case 'text':
        if (block.content.text && block.content.text.length < 100) {
          suggestions.push({
            id: `${block.id}-headline`,
            type: 'ai_generated',
            title: 'Test Headline Variations',
            description: 'Try different headline approaches for better engagement',
            suggestedChange: {
              variations: [
                'Benefit-focused: "Save 50% on Your Next Purchase"',
                'Urgency-driven: "Limited Time: Don\'t Miss Out"',
                'Question-based: "Ready to Transform Your Business?"'
              ]
            },
            confidence: 80,
            expectedImpact: 'positive',
            impactMagnitude: 12,
          });
        }
        break;

      case 'image':
        suggestions.push({
          id: `${block.id}-image-style`,
          type: 'best_practice',
          title: 'Test Image Style',
          description: 'Compare product shots vs lifestyle images',
          suggestedChange: {
            styles: ['product_focus', 'lifestyle', 'illustration', 'infographic']
          },
          confidence: 65,
          expectedImpact: 'neutral',
          impactMagnitude: 10,
        });
        break;
    }

    return suggestions;
  };

  const getRecommendedTestType = (blockType: string): TestableElement['testType'] => {
    switch (blockType) {
      case 'button': return 'cta';
      case 'text': return 'content';
      case 'image': return 'image';
      default: return 'content';
    }
  };

  const getDefaultSuggestions = (blockType: string): ElementSuggestion[] => {
    switch (blockType) {
      case 'button':
        return [{
          id: `${blockType}-default-cta`,
          type: 'best_practice',
          title: 'Test Button Text',
          description: 'Try different call-to-action phrases',
          suggestedChange: { variations: ['Get Started', 'Learn More', 'Try Now'] },
          confidence: 70,
          expectedImpact: 'positive',
          impactMagnitude: 10,
        }];
      case 'text':
        return [{
          id: `${blockType}-default-content`,
          type: 'best_practice',
          title: 'Test Text Content',
          description: 'Try different messaging approaches',
          suggestedChange: { variations: ['Short & punchy', 'Detailed explanation', 'Question format'] },
          confidence: 65,
          expectedImpact: 'neutral',
          impactMagnitude: 8,
        }];
      case 'image':
        return [{
          id: `${blockType}-default-image`,
          type: 'best_practice',
          title: 'Test Image Style',
          description: 'Try different visual approaches',
          suggestedChange: { styles: ['Product focus', 'Lifestyle', 'Illustration'] },
          confidence: 60,
          expectedImpact: 'neutral',
          impactMagnitude: 7,
        }];
      default:
        return [{
          id: `${blockType}-default`,
          type: 'best_practice',
          title: 'Test Element Variations',
          description: 'Try different approaches for this element',
          suggestedChange: { note: 'Custom variations recommended' },
          confidence: 50,
          expectedImpact: 'neutral',
          impactMagnitude: 5,
        }];
    }
  };

  const calculatePriority = (block: any, suggestions: ElementSuggestion[]): TestableElement['priority'] => {
    const avgConfidence = suggestions.reduce((sum, s) => sum + s.confidence, 0) / suggestions.length;
    const avgImpact = suggestions.reduce((sum, s) => sum + s.impactMagnitude, 0) / suggestions.length;

    const score = (avgConfidence + avgImpact) / 2;

    if (score >= 75) return 'high';
    if (score >= 50) return 'medium';
    return 'low';
  };

  const loadTestHistory = useCallback(async () => {
    // Mock test history - in real app, this would be an API call
    const mockHistory: ABTestHistory = {
      templateId: template.id,
      tests: [
        {
          testId: 'test-1',
          testName: 'CTA Button Color Test',
          createdAt: new Date('2024-01-15'),
          completedAt: new Date('2024-01-22'),
          status: 'completed',
          winner: 'variant-b',
          primaryMetricImprovement: 23.5,
          testedElements: ['button-1'],
          insights: ['Orange buttons performed 23.5% better than blue buttons']
        },
        {
          testId: 'test-2',
          testName: 'Headline Variation Test',
          createdAt: new Date('2024-02-01'),
          completedAt: new Date('2024-02-08'),
          status: 'completed',
          winner: 'variant-a',
          primaryMetricImprovement: 15.2,
          testedElements: ['text-1'],
          insights: ['Question-based headlines increased engagement by 15.2%']
        }
      ],
      patterns: [
        {
          pattern: 'Action-oriented CTAs',
          frequency: 8,
          successRate: 75,
          averageImprovement: 18.3,
          examples: ['Get Started Now', 'Start Free Trial', 'Join Today']
        }
      ],
      recommendations: [
        {
          id: 'rec-1',
          title: 'Test CTA Button Text',
          description: 'Based on similar templates, testing CTA text variations typically shows 15-25% improvement',
          basedOn: ['test-1', 'test-2'],
          confidence: 85,
          expectedImprovement: 20,
          suggestedElements: ['button-1', 'button-2']
        }
      ]
    };

    setTestHistory(mockHistory);
  }, [template.id]);

  // Enhanced element selection handlers
  const handleMarkElement = useCallback((element: TestableElement) => {
    setTestableElements(prev =>
      prev.map(el =>
        el.blockId === element.blockId
          ? { ...el, isMarked: !el.isMarked }
          : el
      )
    );
    onMarkTestableElement(element.blockId, element.testType);
  }, [onMarkTestableElement]);

  const handleBulkSelectGroup = useCallback((groupId: string) => {
    const group = elementGroups.find(g => g.id === groupId);
    if (!group) return;

    const shouldSelect = !group.isSelected;

    // Update group selection
    setElementGroups(prev =>
      prev.map(g =>
        g.id === groupId ? { ...g, isSelected: shouldSelect } : g
      )
    );

    // Update element selection
    setTestableElements(prev =>
      prev.map(el => {
        if (group.elements.some(ge => ge.blockId === el.blockId)) {
          return { ...el, isMarked: shouldSelect };
        }
        return el;
      })
    );
  }, [elementGroups]);

  const handleSelectAll = useCallback(() => {
    const allSelected = testableElements.every(el => el.isMarked);
    setTestableElements(prev =>
      prev.map(el => ({ ...el, isMarked: !allSelected }))
    );
  }, [testableElements]);

  const handleClearSelection = useCallback(() => {
    setTestableElements(prev =>
      prev.map(el => ({ ...el, isMarked: false }))
    );
    setElementGroups(prev =>
      prev.map(g => ({ ...g, isSelected: false }))
    );
  }, []);

  // Configuration handlers
  const handleConfigurationChange = useCallback((updates: Partial<ABTestConfiguration>) => {
    setTestConfiguration(prev => ({ ...prev, ...updates }));
  }, []);

  const addSuccessMetric = useCallback(() => {
    const newMetric: SuccessMetric = {
      id: `metric-${Date.now()}`,
      name: 'New Metric',
      type: 'click_rate',
      isPrimary: false,
      weight: 1,
    };

    setTestConfiguration(prev => ({
      ...prev,
      successMetrics: [...prev.successMetrics, newMetric]
    }));
  }, []);

  const updateSuccessMetric = useCallback((metricId: string, updates: Partial<SuccessMetric>) => {
    setTestConfiguration(prev => ({
      ...prev,
      successMetrics: prev.successMetrics.map(metric =>
        metric.id === metricId ? { ...metric, ...updates } : metric
      )
    }));
  }, []);

  const removeSuccessMetric = useCallback((metricId: string) => {
    setTestConfiguration(prev => ({
      ...prev,
      successMetrics: prev.successMetrics.filter(metric => metric.id !== metricId)
    }));
  }, []);

  // Variant Editor handlers
  const handleCreateVariants = useCallback(() => {
    const markedElements = testableElements.filter(el => el.isMarked);

    if (markedElements.length === 0) {
      alert('Please mark at least one element for testing');
      return;
    }

    // Create initial variants
    const controlVariant: ABTestVariant = {
      id: 'control',
      name: 'Control (Original)',
      isControl: true,
      trafficAllocation: 50,
      template: { ...template },
      changes: [],
    };

    const testVariant: ABTestVariant = {
      id: 'variant-a',
      name: 'Variant A',
      isControl: false,
      trafficAllocation: 50,
      template: { ...template },
      changes: [],
    };

    setTestVariants([controlVariant, testVariant]);
    setActiveVariantId('variant-a');
    setShowVariantEditor(true);
  }, [template, testableElements]);

  const handleVariantChange = useCallback((variantId: string, changes: VariantChange[]) => {
    setTestVariants(prev =>
      prev.map(variant => {
        if (variant.id === variantId) {
          // Apply changes to the variant template
          const updatedTemplate = { ...variant.template };

          changes.forEach(change => {
            const blockIndex = updatedTemplate.content.findIndex(block => block.id === change.blockId);
            if (blockIndex !== -1) {
              const updatedBlock = { ...updatedTemplate.content[blockIndex] };

              switch (change.changeType) {
                case 'content':
                  updatedBlock.content = { ...updatedBlock.content, ...change.newValue };
                  break;
                case 'style':
                  updatedBlock.styles = { ...updatedBlock.styles, ...change.newValue };
                  break;
                case 'position':
                  updatedBlock.position = { ...updatedBlock.position, ...change.newValue };
                  break;
                case 'visibility':
                  updatedBlock.metadata.hidden = change.newValue;
                  break;
              }

              updatedTemplate.content[blockIndex] = updatedBlock;
            }
          });

          return {
            ...variant,
            template: updatedTemplate,
            changes: [...variant.changes, ...changes],
          };
        }
        return variant;
      })
    );
  }, []);

  const handleCreateABTest = useCallback(() => {
    const markedElements = testableElements.filter(el => el.isMarked);

    if (markedElements.length === 0) {
      alert('Please mark at least one element for testing');
      return;
    }

    if (!testName.trim()) {
      alert('Please enter a test name');
      return;
    }

    const testConfig: Partial<ABTest> = {
      name: testName,
      templateId: template.id,
      testableElements: markedElements,
      configuration: testConfiguration,
      variants: testVariants.length > 0 ? testVariants : undefined,
      metadata: {
        description: testDescription || `Testing ${markedElements.map(el => el.elementName).join(', ')}`,
        tags: ['auto-generated'],
        category: template.category,
      }
    };

    onCreateTest(testConfig);

    // Transition to A/B testing view with context
    const context = {
      templateId: template.id,
      markedElements,
      suggestions: markedElements.flatMap(el => el.suggestions),
      configuration: testConfiguration,
      variants: testVariants,
    };

    onTransitionToABTestView('new-test', context);
  }, [template, testableElements, testConfiguration, testName, testDescription, testVariants, onCreateTest, onTransitionToABTestView]);

  // Filtered elements based on search and priority filter
  const filteredElements = useMemo(() => {
    return testableElements.filter(element => {
      const matchesSearch = searchQuery === '' ||
        element.elementName.toLowerCase().includes(searchQuery.toLowerCase()) ||
        element.blockType.toLowerCase().includes(searchQuery.toLowerCase());

      const matchesPriority = filterPriority === 'all' || element.priority === filterPriority;

      return matchesSearch && matchesPriority;
    });
  }, [testableElements, searchQuery, filterPriority]);

  const selectedElementsCount = testableElements.filter(el => el.isMarked).length;

  const getPriorityColor = (priority) =>
  // One urgency ramp for the whole app (critical → high → medium → low).
  // Five files each had their own and they disagreed on what "high" and
  // "low" look like; see toneForPriority.
  tone(toneForPriority(priority)).badge;

  if (!isVisible) return null;

  return (
    <div 
      className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4"
      onClick={onClose}
    >
      <div 
        className="bg-surface rounded-lg shadow-xl max-w-7xl w-full max-h-[95vh] overflow-hidden flex flex-col"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Enhanced Header with Template Context */}
        <div className="px-6 py-4 border-b border-line">
          <div className="flex items-center justify-between">
            <div className="flex-1">
              <div className="flex items-center space-x-4">
                <div>
                  <h2 className="text-xl font-semibold text-content">A/B Test Setup</h2>
                  <p className="text-sm text-content-secondary mt-1">
                    Template: "{template?.name || 'Demo Template'}" • {template?.category || 'promotional'}
                  </p>
                </div>
                {aiAnalysis && (
                  <div className="flex items-center space-x-2 px-3 py-2 bg-blue-50 dark:bg-blue-900/30 rounded-lg">
                    <div className="w-2 h-2 bg-blue-500 rounded-full animate-pulse"></div>
                    <span className="text-sm font-medium text-blue-700 dark:text-blue-300">
                      AI Score: {aiAnalysis.overallScore}/100
                    </span>
                    <span className="text-xs text-blue-600 dark:text-blue-300">
                      Est. Impact: +{aiAnalysis.estimatedImpact}%
                    </span>
                  </div>
                )}
              </div>
            </div>
            <button
              onClick={onClose}
              className="text-content-subtle hover:text-content-secondary transition-colors p-2 hover:bg-gray-100 dark:hover:bg-dark-700 rounded-lg"
            >
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>

          {/* Panel Navigation */}
          <div className="flex space-x-1 mt-4 bg-gray-100 dark:bg-dark-700 p-1 rounded-lg">
            {[
              { id: 'elements', label: 'Elements', icon: '🎯' },
              { id: 'configuration', label: 'Configuration', icon: '⚙️' },
              { id: 'insights', label: 'Insights', icon: '📊' }
            ].map((panel) => (
              <button
                key={panel.id}
                onClick={() => setActivePanel(panel.id as any)}
                className={`flex-1 flex items-center justify-center space-x-2 px-4 py-2 rounded-md text-sm font-medium transition-colors ${activePanel === panel.id
                  ? ' text-blue-600 dark:text-blue-300 shadow-sm'
                  : 'text-content-secondary hover:text-content'
                  }`}
              >
                <span>{panel.icon}</span>
                <span>{panel.label}</span>
                {panel.id === 'elements' && selectedElementsCount > 0 && (
                  <span className="bg-blue-100 dark:bg-blue-900/30 text-blue-600 dark:text-blue-300 text-xs px-2 py-1 rounded-full">
                    {selectedElementsCount}
                  </span>
                )}
              </button>
            ))}
          </div>
        </div>

        {/* Three-Panel Content Area */}
        <div className="flex-1 flex overflow-hidden">
          {/* Panel 1: Testable Elements */}
          {activePanel === 'elements' && (
            <div className="w-full flex">
              {/* Left: Element List */}
              <div className="w-2/3 border-r border-line overflow-y-auto">
                <div className="p-6">
                  {/* Empty Template State */}
                  {(!template?.content || template.content.length === 0) && (
                    <div className="text-center py-12">
                      <div className="w-16 h-16 mx-auto mb-4 bg-gray-100 dark:bg-dark-700 rounded-full flex items-center justify-center">
                        <svg className="w-8 h-8 text-content-subtle" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6V4m0 2a2 2 0 100 4m0-4a2 2 0 110 4m-6 8a2 2 0 100-4m0 4a2 2 0 100 4m0-4v2m0-6V4m6 6v10m6-2a2 2 0 100-4m0 4a2 2 0 100 4m0-4v2m0-6V4" />
                        </svg>
                      </div>
                      <h3 className="text-lg font-medium text-content mb-2">Template is Empty</h3>
                      <p className="text-content-secondary mb-6 max-w-md mx-auto">
                        To create an A/B test, you need to add content blocks to your template first.
                        Add elements like text, buttons, or images that you want to test.
                      </p>
                      <div className="space-y-3">
                        <button
                          onClick={onClose}
                          className="inline-flex items-center px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
                        >
                          <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
                          </svg>
                          Add Content to Template
                        </button>
                        <div className="text-sm text-content-muted">
                          Or continue with demo elements below to explore A/B testing features
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Content when template has elements OR demo mode */}
                  {(template?.content?.length > 0 || testableElements.length > 0) && (
                    <div>
                      {/* Search and Filter Controls */}
                      <div className="mb-6 space-y-4">
                        <div className="flex items-center space-x-4">
                          <div className="flex-1 relative">
                            <input
                              type="text"
                              placeholder="Search elements..."
                              value={searchQuery}
                              onChange={(e) => setSearchQuery(e.target.value)}
                              className="w-full pl-10 pr-4 py-2 border border-line-strong rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                            />
                            <svg className="w-5 h-5 text-content-subtle absolute left-3 top-2.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                            </svg>
                          </div>
                          <SelectField
                            value={filterPriority}
                            onChange={(e) => setFilterPriority(e.target.value as any)}
                            className="px-3 py-2 border border-line-strong rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                          >
                            <option value="all">All Priority</option>
                            <option value="high">High Priority</option>
                            <option value="medium">Medium Priority</option>
                            <option value="low">Low Priority</option>
                          </SelectField>
                        </div>

                        {/* Bulk Selection Controls */}
                        <div className="flex items-center justify-between">
                          <div className="flex items-center space-x-2">
                            <button
                              onClick={() => setBulkSelectMode(!bulkSelectMode)}
                              className={`px-3 py-1 text-sm rounded-md transition-colors ${bulkSelectMode
                                ? 'bg-blue-100 text-blue-700'
                                : 'bg-gray-100 dark:bg-dark-700 text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-dark-600'
                                }`}
                            >
                              Bulk Select
                            </button>
                            {bulkSelectMode && (
                              <>
                                <button
                                  onClick={handleSelectAll}
                                  className="px-3 py-1 text-sm text-blue-600 dark:text-blue-300 hover:text-blue-700 dark:hover:text-blue-300"
                                >
                                  Select All
                                </button>
                                <button
                                  onClick={handleClearSelection}
                                  className="px-3 py-1 text-sm text-content-secondary hover:text-gray-700 dark:hover:text-gray-300"
                                >
                                  Clear
                                </button>
                              </>
                            )}
                          </div>
                          <div className="text-sm text-content-secondary">
                            {selectedElementsCount} of {filteredElements.length} selected
                          </div>
                        </div>

                        {/* Element Groups */}
                        {bulkSelectMode && elementGroups.length > 0 && (
                          <div className="border border-line rounded-lg p-4">
                            <h4 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-3">Quick Select Groups</h4>
                            <div className="grid grid-cols-2 gap-2">
                              {elementGroups.map((group) => (
                                <button
                                  key={group.id}
                                  onClick={() => handleBulkSelectGroup(group.id)}
                                  className={`p-2 text-sm rounded-md border transition-colors ${group.isSelected
                                    ? 'border-blue-300 dark:border-blue-800/50 bg-blue-50 dark:bg-blue-900/30 text-blue-700'
                                    : 'border-line hover:border-line-strong text-gray-700 dark:text-gray-300'
                                    }`}
                                >
                                  {group.name} ({group.elements.length})
                                </button>
                              ))}
                            </div>
                          </div>
                        )}
                      </div>

                      {/* Elements List */}
                      <div className="space-y-3">
                        {filteredElements.map((element) => (
                          <div
                            key={element.blockId}
                            className={`border rounded-lg p-4 transition-all ${element.isMarked
                              ? 'border-blue-300 dark:border-blue-800/50 bg-blue-50 dark:bg-blue-900/30'
                              : 'border-line hover:border-line-strong'
                              }`}
                          >
                            <div className="flex items-start justify-between">
                              <div className="flex-1">
                                <div className="flex items-center space-x-3">
                                  <input
                                    type="checkbox"
                                    checked={element.isMarked}
                                    onChange={() => handleMarkElement(element)}
                                    className="rounded border-line-strong text-blue-600 dark:text-blue-300 focus:ring-blue-500"
                                  />
                                  <div>
                                    <h4 className="font-medium text-content">{element.elementName}</h4>
                                    <p className="text-sm text-content-secondary">
                                      {element.blockType} • {element.testType} test
                                    </p>
                                  </div>
                                </div>
                              </div>
                              <div className="flex items-center space-x-2">
                                <span className={`px-2 py-1 text-xs rounded-full border ${getPriorityColor(element.priority)}`}>
                                  {element.priority}
                                </span>
                                {element.suggestions.length > 0 && (
                                  <span className="text-xs text-blue-600 dark:text-blue-300 bg-blue-100 dark:bg-blue-900/30 px-2 py-1 rounded-full">
                                    {element.suggestions.length} suggestions
                                  </span>
                                )}
                              </div>
                            </div>

                            {showAIAnalysis && element.suggestions.length > 0 && (
                              <div className="mt-3 pt-3 border-t border-line-subtle">
                                <h5 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">AI Suggestions:</h5>
                                <div className="space-y-2">
                                  {element.suggestions.slice(0, 2).map((suggestion) => (
                                    <div key={suggestion.id} className="text-sm">
                                      <div className="flex items-center justify-between">
                                        <span className="font-medium text-gray-800 dark:text-gray-200">{suggestion.title}</span>
                                        <div className="flex items-center space-x-2">
                                          <span className="text-xs text-content-muted">{suggestion.confidence}% confidence</span>
                                          <span className={`text-xs px-2 py-1 rounded-full ${suggestion.expectedImpact === 'positive'
                                            ? 'bg-green-100 text-green-700'
                                            : suggestion.expectedImpact === 'negative'
                                              ? 'bg-red-100 text-red-700'
                                              : 'bg-gray-100 dark:bg-dark-700 text-gray-700 dark:text-gray-300'
                                            }`}>
                                            {suggestion.expectedImpact}
                                          </span>
                                        </div>
                                      </div>
                                      <p className="text-content-secondary text-xs mt-1">{suggestion.description}</p>
                                    </div>
                                  ))}
                                  {element.suggestions.length > 2 && (
                                    <p className="text-xs text-blue-600 dark:text-blue-300">
                                      +{element.suggestions.length - 2} more suggestions
                                    </p>
                                  )}
                                </div>
                              </div>
                            )}
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              </div>

              {/* Right: AI Analysis Panel */}
              <div className="w-1/3 bg-gray-50 dark:bg-dark-700 overflow-y-auto">
                <div className="p-6">
                  <div className="flex items-center justify-between mb-4">
                    <h3 className="text-lg font-medium text-content">AI Analysis</h3>
                    <button
                      onClick={() => setShowAIAnalysis(!showAIAnalysis)}
                      className="text-sm text-blue-600 dark:text-blue-300 hover:text-blue-700 dark:hover:text-blue-300"
                    >
                      {showAIAnalysis ? 'Hide' : 'Show'} Details
                    </button>
                  </div>

                  {aiAnalysis ? (
                    <div className="space-y-4">
                      {/* Overall Score */}
                      <div className="bg-surface rounded-lg p-4 border border-line">
                        <div className="flex items-center justify-between mb-2">
                          <span className="text-sm font-medium text-gray-700 dark:text-gray-300">Test Potential</span>
                          <span className="text-lg font-bold text-blue-600 dark:text-blue-300">{aiAnalysis.overallScore}/100</span>
                        </div>
                        <div className="w-full bg-gray-200 dark:bg-dark-600 rounded-full h-2">
                          <div
                            className="bg-blue-600 h-2 rounded-full transition-all duration-300"
                            style={{ width: `${aiAnalysis.overallScore}%` }}
                          ></div>
                        </div>
                      </div>

                      {/* Recommendations */}
                      <div className="bg-surface rounded-lg p-4 border border-line">
                        <h4 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-3">AI Recommendations</h4>
                        <div className="space-y-2">
                          {aiAnalysis.recommendations.map((rec, index) => (
                            <div key={index} className="flex items-start space-x-2">
                              <div className="w-1.5 h-1.5 bg-blue-500 rounded-full mt-2 flex-shrink-0"></div>
                              <p className="text-sm text-content-secondary">{rec}</p>
                            </div>
                          ))}
                        </div>
                      </div>

                      {/* Priority Elements */}
                      {aiAnalysis.priorityElements.length > 0 && (
                        <div className="bg-surface rounded-lg p-4 border border-line">
                          <h4 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-3">High Priority Elements</h4>
                          <div className="space-y-2">
                            {aiAnalysis.priorityElements.map((element) => (
                              <div key={element.blockId} className="flex items-center justify-between">
                                <span className="text-sm text-gray-800 dark:text-gray-200">{element.elementName}</span>
                                <span className="text-xs text-red-600 dark:text-red-300 bg-red-50 dark:bg-red-900/30 px-2 py-1 rounded-full">
                                  High Impact
                                </span>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>
                  ) : (
                    <div className="space-y-4">
                      {/* Loading State */}
                      <div className="bg-surface rounded-lg p-4 border border-line">
                        <div className="flex items-center space-x-3">
                          <div className="w-6 h-6 border-2 border-blue-600 border-t-transparent rounded-full animate-spin"></div>
                          <span className="text-sm text-content-secondary">Analyzing template for A/B testing opportunities...</span>
                        </div>
                      </div>
                      
                      {/* Placeholder for when no elements are found */}
                      {testableElements.length === 0 && (
                        <div className="bg-surface rounded-lg p-4 border border-line">
                          <div className="text-center py-4">
                            <div className="w-12 h-12 mx-auto mb-3 bg-gray-100 dark:bg-dark-700 rounded-full flex items-center justify-center">
                              <svg className="w-6 h-6 text-content-subtle" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z" />
                              </svg>
                            </div>
                            <p className="text-sm text-content-secondary">No testable elements found</p>
                            <p className="text-xs text-content-muted mt-1">Add content to your template to see AI recommendations</p>
                          </div>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}

          {/* Panel 2: Test Configuration */}
          {activePanel === 'configuration' && (
            <div className="w-full overflow-y-auto">
              <div className="p-6 max-w-4xl mx-auto">
                <div className="space-y-8">
                  {/* Test Basic Info */}
                  <div className="bg-surface rounded-lg border border-line p-6">
                    <h3 className="text-lg font-medium text-content mb-4">Test Information</h3>
                    <div className="grid grid-cols-1 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Test Name</label>
                        <input
                          type="text"
                          value={testName}
                          onChange={(e) => setTestName(e.target.value)}
                          className="w-full px-3 py-2 border border-line-strong rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                          placeholder="Enter test name..."
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Description (Optional)</label>
                        <textarea
                          value={testDescription}
                          onChange={(e) => setTestDescription(e.target.value)}
                          rows={3}
                          className="w-full px-3 py-2 border border-line-strong rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                          placeholder="Describe what you're testing and why..."
                        />
                      </div>
                    </div>
                  </div>

                  {/* Traffic Split */}
                  <div className="bg-surface rounded-lg border border-line p-6">
                    <h3 className="text-lg font-medium text-content mb-4">Traffic Allocation</h3>
                    <div className="space-y-4">
                      <div className="flex items-center space-x-4">
                        <div className="flex-1">
                          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Control (A)</label>
                          <div className="flex items-center space-x-2">
                            <input
                              type="range"
                              min="10"
                              max="90"
                              value={testConfiguration.trafficSplit[0]}
                              onChange={(e) => {
                                const value = parseInt(e.target.value);
                                handleConfigurationChange({
                                  trafficSplit: [value, 100 - value]
                                });
                              }}
                              className="flex-1"
                            />
                            <span className="text-sm font-medium text-gray-700 dark:text-gray-300 w-12">
                              {testConfiguration.trafficSplit[0]}%
                            </span>
                          </div>
                        </div>
                        <div className="flex-1">
                          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Variant (B)</label>
                          <div className="flex items-center space-x-2">
                            <input
                              type="range"
                              min="10"
                              max="90"
                              value={testConfiguration.trafficSplit[1]}
                              onChange={(e) => {
                                const value = parseInt(e.target.value);
                                handleConfigurationChange({
                                  trafficSplit: [100 - value, value]
                                });
                              }}
                              className="flex-1"
                            />
                            <span className="text-sm font-medium text-gray-700 dark:text-gray-300 w-12">
                              {testConfiguration.trafficSplit[1]}%
                            </span>
                          </div>
                        </div>
                      </div>
                      <div className="bg-gray-50 dark:bg-dark-700 rounded-lg p-3">
                        <div className="flex h-4 rounded-full overflow-hidden">
                          <div
                            className="bg-blue-500 flex items-center justify-center text-xs text-white font-medium"
                            style={{ width: `${testConfiguration.trafficSplit[0]}%` }}
                          >
                            {testConfiguration.trafficSplit[0] > 15 ? 'A' : ''}
                          </div>
                          <div
                            className="bg-green-500 flex items-center justify-center text-xs text-white font-medium"
                            style={{ width: `${testConfiguration.trafficSplit[1]}%` }}
                          >
                            {testConfiguration.trafficSplit[1] > 15 ? 'B' : ''}
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Success Metrics */}
                  <div className="bg-surface rounded-lg border border-line p-6">
                    <div className="flex items-center justify-between mb-4">
                      <h3 className="text-lg font-medium text-content">Success Metrics</h3>
                      <button
                        onClick={addSuccessMetric}
                        className="px-3 py-1 text-sm bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors"
                      >
                        Add Metric
                      </button>
                    </div>
                    <div className="space-y-4">
                      {testConfiguration.successMetrics.map((metric, _index) => (
                        <div key={metric.id} className="border border-line rounded-lg p-4">
                          <div className="flex items-center justify-between mb-3">
                            <div className="flex items-center space-x-2">
                              <input
                                type="text"
                                value={metric.name}
                                onChange={(e) => updateSuccessMetric(metric.id, { name: e.target.value })}
                                className="font-medium text-content bg-transparent border-none p-0 focus:ring-0"
                              />
                              {metric.isPrimary && (
                                <span className="text-xs bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300 px-2 py-1 rounded-full">
                                  Primary
                                </span>
                              )}
                            </div>
                            {testConfiguration.successMetrics.length > 1 && (
                              <button
                                onClick={() => removeSuccessMetric(metric.id)}
                                className="text-red-600 dark:text-red-300 hover:text-red-700 dark:hover:text-red-300 text-sm"
                              >
                                Remove
                              </button>
                            )}
                          </div>
                          <div className="grid grid-cols-3 gap-4">
                            <div>
                              <label className="block text-sm text-content-secondary mb-1">Type</label>
                              <SelectField
                                value={metric.type}
                                onChange={(e) => updateSuccessMetric(metric.id, { type: e.target.value as any })}
                                className="w-full px-3 py-2 text-sm border border-line-strong rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                              >
                                <option value="click_rate">Click Rate</option>
                                <option value="open_rate">Open Rate</option>
                                <option value="conversion_rate">Conversion Rate</option>
                                <option value="revenue">Revenue</option>
                                <option value="custom">Custom</option>
                              </SelectField>
                            </div>
                            <div>
                              <label className="block text-sm text-content-secondary mb-1">Weight</label>
                              <input
                                type="number"
                                min="0.1"
                                max="10"
                                step="0.1"
                                value={metric.weight}
                                onChange={(e) => updateSuccessMetric(metric.id, { weight: parseFloat(e.target.value) })}
                                className="w-full px-3 py-2 text-sm border border-line-strong rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                              />
                            </div>
                            <div className="flex items-end">
                              <label className="flex items-center space-x-2">
                                <input
                                  type="checkbox"
                                  checked={metric.isPrimary}
                                  onChange={(e) => updateSuccessMetric(metric.id, { isPrimary: e.target.checked })}
                                  className="rounded border-line-strong text-blue-600 dark:text-blue-300 focus:ring-blue-500"
                                />
                                <span className="text-sm text-content-secondary">Primary metric</span>
                              </label>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>

                  {/* Statistical Settings */}
                  <div className="bg-surface rounded-lg border border-line p-6">
                    <h3 className="text-lg font-medium text-content mb-4">Statistical Settings</h3>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                          Confidence Level
                        </label>
                        <SelectField
                          value={testConfiguration.statisticalSettings.confidenceLevel}
                          onChange={(e) => handleConfigurationChange({
                            statisticalSettings: {
                              ...testConfiguration.statisticalSettings,
                              confidenceLevel: parseInt(e.target.value)
                            }
                          })}
                          className="w-full px-3 py-2 border border-line-strong rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                        >
                          <option value={90}>90%</option>
                          <option value={95}>95%</option>
                          <option value={99}>99%</option>
                        </SelectField>
                        <p className="text-xs text-content-muted mt-1">
                          Higher = more certainty, longer test
                        </p>
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                          Min. Detectable Effect
                        </label>
                        <div className="relative">
                          <input
                            type="number"
                            min="1"
                            max="50"
                            value={testConfiguration.statisticalSettings.minimumDetectableEffect}
                            onChange={(e) => handleConfigurationChange({
                              statisticalSettings: {
                                ...testConfiguration.statisticalSettings,
                                minimumDetectableEffect: parseInt(e.target.value)
                              }
                            })}
                            className="w-full px-3 py-2 pr-8 border border-line-strong rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                          />
                          <span className="absolute right-3 top-2 text-content-muted text-sm">%</span>
                        </div>
                        <p className="text-xs text-content-muted mt-1">
                          Smallest improvement to detect
                        </p>
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                          Statistical Power
                        </label>
                        <SelectField
                          value={testConfiguration.statisticalSettings.powerAnalysis}
                          onChange={(e) => handleConfigurationChange({
                            statisticalSettings: {
                              ...testConfiguration.statisticalSettings,
                              powerAnalysis: parseInt(e.target.value)
                            }
                          })}
                          className="w-full px-3 py-2 border border-line-strong rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                        >
                          <option value={80}>80%</option>
                          <option value={90}>90%</option>
                          <option value={95}>95%</option>
                        </SelectField>
                        <p className="text-xs text-content-muted mt-1">
                          Probability of detecting true effect
                        </p>
                      </div>
                    </div>
                  </div>

                  {/* Test Duration */}
                  <div className="bg-surface rounded-lg border border-line p-6">
                    <h3 className="text-lg font-medium text-content mb-4">Test Duration</h3>
                    <div className="space-y-4">
                      <div className="flex space-x-4">
                        {[
                          { value: 'until_significant', label: 'Until Significant', desc: 'Stop when results are statistically significant' },
                          { value: 'fixed', label: 'Fixed Duration', desc: 'Run for a specific number of days' },
                          { value: 'custom', label: 'Custom End Date', desc: 'Set a specific end date' }
                        ].map((option) => (
                          <label key={option.value} className="flex-1">
                            <input
                              type="radio"
                              name="durationType"
                              value={option.value}
                              checked={testConfiguration.duration.type === option.value}
                              onChange={(e) => handleConfigurationChange({
                                duration: { ...testConfiguration.duration, type: e.target.value as any }
                              })}
                              className="sr-only"
                            />
                            <div className={`p-4 border-2 rounded-lg cursor-pointer transition-colors ${testConfiguration.duration.type === option.value
                              ? 'border-blue-500 bg-blue-50 dark:bg-blue-900/30'
                              : 'border-line hover:border-line-strong'
                              }`}>
                              <div className="font-medium text-content">{option.label}</div>
                              <div className="text-sm text-content-secondary mt-1">{option.desc}</div>
                            </div>
                          </label>
                        ))}
                      </div>

                      {testConfiguration.duration.type === 'fixed' && (
                        <div className="max-w-xs">
                          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                            Duration (days)
                          </label>
                          <input
                            type="number"
                            min="1"
                            max="90"
                            value={testConfiguration.duration.days || 14}
                            onChange={(e) => handleConfigurationChange({
                              duration: { ...testConfiguration.duration, days: parseInt(e.target.value) }
                            })}
                            className="w-full px-3 py-2 border border-line-strong rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                          />
                        </div>
                      )}

                      {testConfiguration.duration.type === 'custom' && (
                        <div className="max-w-xs">
                          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                            End Date
                          </label>
                          <input
                            type="date"
                            value={testConfiguration.duration.endDate?.toISOString().split('T')[0] || ''}
                            onChange={(e) => handleConfigurationChange({
                              duration: { ...testConfiguration.duration, endDate: new Date(e.target.value) }
                            })}
                            className="w-full px-3 py-2 border border-line-strong rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                          />
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Panel 3: Historical Insights */}
          {activePanel === 'insights' && (
            <div className="w-full overflow-y-auto">
              <div className="p-6">
                <h3 className="text-lg font-medium text-content mb-6">Historical Insights & Recommendations</h3>

                {testHistory && (
                  <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                    {/* Recent Tests */}
                    <div className="bg-surface rounded-lg border border-line p-6">
                      <h4 className="text-lg font-medium text-content mb-4">Recent Tests</h4>
                      <div className="space-y-4">
                        {testHistory.tests.slice(0, 5).map((test) => (
                          <div key={test.testId} className="border border-line rounded-lg p-4">
                            <div className="flex items-center justify-between mb-2">
                              <h5 className="font-medium text-content">{test.testName}</h5>
                              <span className={`px-2 py-1 text-xs rounded-full ${test.status === 'completed'
                                ? 'bg-green-100 dark:bg-green-900/30 text-green-800 dark:text-green-300'
                                : 'bg-yellow-100 dark:bg-yellow-900/30 text-yellow-800 dark:text-yellow-300'
                                }`}>
                                {test.status}
                              </span>
                            </div>
                            {test.winner && (
                              <div className="flex items-center space-x-2 mb-2">
                                <svg className="w-4 h-4 text-green-500 dark:text-green-400" fill="currentColor" viewBox="0 0 20 20">
                                  <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                                </svg>
                                <span className="text-sm text-green-600 dark:text-green-300 font-medium">
                                  +{test.primaryMetricImprovement}% improvement
                                </span>
                              </div>
                            )}
                            <div className="text-xs text-content-muted">
                              {test.createdAt.toLocaleDateString()} - {test.completedAt?.toLocaleDateString()}
                            </div>
                            {test.insights.length > 0 && (
                              <div className="mt-2">
                                <p className="text-sm text-content-secondary">{test.insights[0]}</p>
                              </div>
                            )}
                          </div>
                        ))}
                      </div>
                    </div>

                    {/* AI Recommendations */}
                    <div className="bg-surface rounded-lg border border-line p-6">
                      <h4 className="text-lg font-medium text-content mb-4">AI Recommendations</h4>
                      <div className="space-y-4">
                        {testHistory.recommendations.map((rec) => (
                          <div key={rec.id} className="border border-blue-200 dark:border-blue-800/50 rounded-lg p-4 bg-blue-50 dark:bg-blue-900/30">
                            <div className="flex items-start justify-between mb-2">
                              <h5 className="font-medium text-blue-900 dark:text-blue-300">{rec.title}</h5>
                              <span className="text-xs text-blue-600 dark:text-blue-300 bg-blue-100 dark:bg-blue-900/30 px-2 py-1 rounded-full">
                                {rec.confidence}% confidence
                              </span>
                            </div>
                            <p className="text-sm text-blue-800 dark:text-blue-300 mb-2">{rec.description}</p>
                            <div className="flex items-center justify-between">
                              <span className="text-xs text-blue-600 dark:text-blue-300">
                                Expected improvement: +{rec.expectedImprovement}%
                              </span>
                              <button className="text-xs text-blue-600 dark:text-blue-300 hover:text-blue-700 dark:hover:text-blue-300 font-medium">
                                Apply Suggestion
                              </button>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>

                    {/* Success Patterns */}
                    <div className="bg-surface rounded-lg border border-line p-6">
                      <h4 className="text-lg font-medium text-content mb-4">Success Patterns</h4>
                      <div className="space-y-4">
                        {testHistory.patterns.map((pattern, index) => (
                          <div key={index} className="border border-line rounded-lg p-4">
                            <div className="flex items-center justify-between mb-2">
                              <h5 className="font-medium text-content">{pattern.pattern}</h5>
                              <span className="text-sm text-green-600 dark:text-green-300 font-medium">
                                {pattern.successRate}% success rate
                              </span>
                            </div>
                            <div className="text-sm text-content-secondary mb-2">
                              Average improvement: +{pattern.averageImprovement}%
                            </div>
                            <div className="text-xs text-content-muted">
                              Based on {pattern.frequency} tests
                            </div>
                            <div className="mt-2">
                              <div className="text-xs text-content-secondary mb-1">Examples:</div>
                              <div className="flex flex-wrap gap-1">
                                {pattern.examples.slice(0, 3).map((example, i) => (
                                  <span key={i} className="text-xs bg-gray-100 dark:bg-dark-700 text-gray-700 dark:text-gray-300 px-2 py-1 rounded">
                                    {example}
                                  </span>
                                ))}
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>

                    {/* Performance Overview */}
                    <div className="bg-surface rounded-lg border border-line p-6">
                      <h4 className="text-lg font-medium text-content mb-4">Template Performance</h4>
                      <div className="space-y-4">
                        <div className="grid grid-cols-2 gap-4">
                          <div className="text-center p-4 bg-gray-50 dark:bg-dark-700 rounded-lg">
                            <div className="text-2xl font-bold text-content">
                              {testHistory.tests.length}
                            </div>
                            <div className="text-sm text-content-secondary">Total Tests</div>
                          </div>
                          <div className="text-center p-4 bg-green-50 dark:bg-green-900/30 rounded-lg">
                            <div className="text-2xl font-bold text-green-600 dark:text-green-300">
                              {Math.round(testHistory.tests.filter(t => t.winner).reduce((sum, t) => sum + t.primaryMetricImprovement, 0) / testHistory.tests.filter(t => t.winner).length || 0)}%
                            </div>
                            <div className="text-sm text-content-secondary">Avg. Improvement</div>
                          </div>
                        </div>
                        <div className="border-t border-line pt-4">
                          <div className="text-sm text-content-secondary mb-2">Most Tested Elements:</div>
                          <div className="space-y-1">
                            {['CTA Buttons', 'Headlines', 'Images'].map((element, index) => (
                              <div key={element} className="flex items-center justify-between">
                                <span className="text-sm text-gray-800 dark:text-gray-200">{element}</span>
                                <span className="text-xs text-content-muted">{3 - index} tests</span>
                              </div>
                            ))}
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </div>
          )}
        </div>

        {/* Enhanced Footer */}
        <div className="px-6 py-4 border-t border-line bg-gray-50 dark:bg-dark-700">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-4">
              <div className="text-sm text-content-secondary">
                {selectedElementsCount} elements selected
              </div>
              {aiAnalysis && (
                <div className="text-sm text-blue-600 dark:text-blue-300">
                  Expected impact: +{aiAnalysis.estimatedImpact}%
                </div>
              )}
              {testVariants.length > 0 && (
                <div className="text-sm text-green-600 dark:text-green-300">
                  {testVariants.length} variants created
                </div>
              )}
            </div>
            <div className="flex items-center space-x-3">
              <button
                onClick={onClose}
                className="px-4 py-2 text-gray-700 dark:text-gray-300 hover:text-content transition-colors rounded-md hover:bg-gray-100 dark:hover:bg-dark-700"
              >
                Cancel
              </button>
              {selectedElementsCount > 0 && (
                <button
                  onClick={handleCreateVariants}
                  className="px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 transition-colors shadow-sm font-medium"
                >
                  Create Variants
                </button>
              )}
              <button
                onClick={handleCreateABTest}
                disabled={selectedElementsCount === 0 || !testName.trim()}
                className="px-6 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors shadow-sm font-medium"
              >
                {testVariants.length > 0 ? 'Create Test with Variants' : 'Create A/B Test'}
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Variant Editor Modal */}
      {showVariantEditor && testVariants.length > 0 && activeVariantId && (
        <VariantEditor
          controlTemplate={template}
          testVariant={testVariants.find(v => v.id === activeVariantId)!}
          testableElements={testableElements.filter(el => el.isMarked)}
          onVariantChange={handleVariantChange}
          onPreviewDevice={(device) => console.log('Preview device:', device)}
          isVisible={showVariantEditor}
          onClose={() => setShowVariantEditor(false)}
        />
      )}
    </div>
  );
};

export default ABTestManager;